/**
 * OkyanusViewer — Three.js façade viewer core (framework agnostic).
 *
 * Design notes:
 *  - One InstancedMesh per (block definition x geometry part). Repeated Rhino
 *    blocks therefore cost one GPU upload regardless of instance count.
 *  - Status recolouring only rewrites the per-instance colour attribute; no
 *    geometry is rebuilt, so a status change is O(1) per panel.
 *  - Hiding/isolating rewrites a single instance matrix (zero scale).
 */
import * as THREE from "three";
import {
  acceleratedRaycast,
  computeBoundsTree,
  disposeBoundsTree,
} from "three-mesh-bvh";

// Accelerated raycasting for selection / OSNAP / measurement on huge models.
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
THREE.Mesh.prototype.raycast = acceleratedRaycast;
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { ViewCube } from "./ViewCube";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";

const HIGHLIGHT = new THREE.Color("#FF2D95");
const WHITE = new THREE.Color("#FFFFFF");
// Screen-space line widths (pixels) so edges stay constant at any camera distance.
const EDGE_INNER_PX = 1;
const ZERO_MATRIX = new THREE.Matrix4().makeScale(0, 0, 0);

export class OkyanusViewer {
  constructor(container, options = {}) {
    // When false, unselected panels keep their original appearance instead of
    // ghost-fading while a selection is active (used by the Factory workspace).
    this.ghostUnselected = options.ghostUnselected !== false;
    // Non-selectable panels shown muted (Factory: Design/BIM not approved).
    this.locked = new Set();
    this.container = container;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(options.background || "#EEF2F8");

    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
    // 2x on Retina/4K quadruples fragment cost for negligible CAD-viewer gain.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.localClippingEnabled = true;
    this.renderer.domElement.style.display = "block";
    this.renderer.domElement.style.width = "100%";
    this.renderer.domElement.style.height = "100%";
    container.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100000);
    this.camera.position.set(60, -80, 45);
    this.camera.up.set(0, 0, 1);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    // Touch: one finger orbits, two fingers pan + pinch-zoom.
    this.controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    // On-demand rendering: camera movement marks the frame dirty.
    this.controls.addEventListener("change", () => this._markDirty());
    this._dirty = true;
    this.renderer.domElement.style.touchAction = "none";
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.12;

    this.scene.add(new THREE.HemisphereLight(0xffffff, 0xc9d4e4, 1.35));
    const dir = new THREE.DirectionalLight(0xffffff, 1.1);
    dir.position.set(1, -1.4, 2);
    this.scene.add(dir);
    const dir2 = new THREE.DirectionalLight(0xdce8ff, 0.45);
    dir2.position.set(-1.2, 1, 0.6);
    this.scene.add(dir2);

    this.grid = new THREE.GridHelper(400, 40, 0xc3cddd, 0xdde4ee);
    this.grid.rotation.x = Math.PI / 2;
    this.scene.add(this.grid);

    this.root = new THREE.Group();
    this.scene.add(this.root);

    this.meshes = [];
    this.instanceIndex = new Map(); // panelUid -> [{mesh, index}] (ACTIVE representation)
    this.panelMeta = new Map(); // panelUid -> { name, matrix, defIndex }
    // Light (oriented bounding box) vs detailed geometry representations of the
    // SAME panels. A panel is drawn by exactly one of them at any time.
    this.lightMeshes = [];
    this.detailMeshes = [];
    this.lightIndex = new Map();
    this.detailIndex = new Map();
    this.detailLoaded = new Set(); // definition indices whose geometry is in memory
    this.detailShown = new Set(); // panel uids currently drawn in full detail
    this.selection = new Set();
    this.hidden = new Set();
    this.originalColors = new Map();
    this.statusColors = new Map();
    this.compareColors = new Map();
    this.colorMode = "original";
    this.clipPlane = null;
    this.measurePoints = [];
    this.measureGroup = new THREE.Group();
    this.scene.add(this.measureGroup);
    this.issueGroup = new THREE.Group();
    this.scene.add(this.issueGroup);
    this.noteGroup = new THREE.Group();
    this.scene.add(this.noteGroup);
    this.newGroup = new THREE.Group();
    this.scene.add(this.newGroup);
    // Opaque magenta overlay so the selected panel stays vivid while others ghost.
    this.selectionGroup = new THREE.Group();
    this.scene.add(this.selectionGroup);
    this.labelGroup = new THREE.Group();
    this.scene.add(this.labelGroup);
    // Persistent black edges: thin lines per geometry/layer, thicker per panel boundary.
    this.edgeGroup = new THREE.Group();
    this.scene.add(this.edgeGroup);
    this._innerEdgeMaterial = this._lineMaterial("#111827", EDGE_INNER_PX, 0.55);
    this._selInnerMaterial = this._lineMaterial("#111827", EDGE_INNER_PX, 0.9);
    this._selOuterMaterial = this._lineMaterial("#FFFFFF", EDGE_INNER_PX, 0.95);
    this._issueTexture = null;
    this._badgeTextures = {};
    this.bbox = new THREE.Box3();

    this.raycaster = new THREE.Raycaster();
    this.onSelectionChange = null;
    this.onMeasure = null;
    this.mmPerUnit = 1;
    this.mode = "select";

    this._boundResize = () => this.resize();
    window.addEventListener("resize", this._boundResize);
    // The canvas also changes size when surrounding panels (filter bar, table,
    // properties) open/close, which window resize does not report.
    this._resizeObserver = new ResizeObserver(() => this.resize());
    this._resizeObserver.observe(container);
    this._onPointerDown = (event) => this._handlePointerDown(event);
    this._onPointerUp = (event) => this._handlePointerUp(event);
    this._onPointerMove = (event) => this._handlePointerMove(event);
    this.renderer.domElement.addEventListener("pointerdown", this._onPointerDown);
    this.renderer.domElement.addEventListener("pointerup", this._onPointerUp);
    this.renderer.domElement.addEventListener("pointermove", this._onPointerMove);

    // Rubber-band selection overlay (mouse drag box select).
    this.boxEl = document.createElement("div");
    this.boxEl.style.cssText =
      "position:absolute;border:1px solid #0E7490;background:rgba(14,116,144,0.12);pointer-events:none;display:none;z-index:5";
    // Only promote a static container: an inline style would break `absolute inset-0`.
    if (getComputedStyle(container).position === "static") container.style.position = "relative";
    container.appendChild(this.boxEl);
    this._boxStart = null;

    this.viewCube = new ViewCube(container, this.camera, (dir) => this.animateToDirection(dir));

    this.resize();
    this._animate();
  }

  dispose() {
    window.removeEventListener("resize", this._boundResize);
    this._resizeObserver?.disconnect();
    this.renderer.domElement.removeEventListener("pointerdown", this._onPointerDown);
    this.renderer.domElement.removeEventListener("pointerup", this._onPointerUp);
    this.renderer.domElement.removeEventListener("pointermove", this._onPointerMove);
    cancelAnimationFrame(this._frame);
    this.viewCube?.dispose();
    this.meshes.forEach((mesh) => {
      mesh.geometry.dispose();
      mesh.material.dispose();
    });
    this.renderer.dispose();
    while (this.container.firstChild) this.container.removeChild(this.container.firstChild);
  }

  /** Moves the renderer canvas into a new container (viewer keep-alive). */
  reattach(container) {
    this._resizeObserver.disconnect();
    this.container = container;
    container.appendChild(this.renderer.domElement);
    this._resizeObserver.observe(container);
    this.resize();
  }

  resize() {
    const { clientWidth: w, clientHeight: h } = this.container;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.renderer.domElement.style.width = "100%";
    this.renderer.domElement.style.height = "100%";
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    [
      this._innerEdgeMaterial,
      this._selInnerMaterial,
      this._selOuterMaterial,
    ].forEach((material) => material?.resolution.set(w, h));
    this.viewCube?.layout();
    this._markDirty();
  }

  /** Fat-line material: width is in pixels, so it never scales with camera distance. */
  _lineMaterial(color, widthPx, opacity) {
    const material = new LineMaterial({
      color: new THREE.Color(color),
      linewidth: widthPx,
      transparent: true,
      opacity,
    });
    // Edges sit exactly on the surfaces they outline — offset them towards the camera.
    material.polygonOffset = true;
    material.polygonOffsetFactor = -6;
    material.polygonOffsetUnits = -6;
    const { clientWidth: w, clientHeight: h } = this.container;
    material.resolution.set(w || 1, h || 1);
    return material;
  }

  /** Build instanced meshes from loaded definitions + instance list. */
  build(definitions, instances) {
    const groups = new Map();
    instances.forEach((inst) => {
      if (!groups.has(inst.def)) groups.set(inst.def, []);
      groups.get(inst.def).push(inst);
    });

    groups.forEach((list, defIndex) => {
      const definition = definitions[defIndex];
      if (!definition) return;
      definition.parts.forEach((part, partIndex) => {
        const isGlass = /glass|cam/i.test(part.layer || "");
        const material = new THREE.MeshLambertMaterial({
          color: new THREE.Color(part.color || "#B0B7C3"),
          transparent: isGlass,
          opacity: isGlass ? 0.62 : 1,
          side: THREE.DoubleSide,
        });
        const mesh = new THREE.InstancedMesh(part.geometry, material, list.length);
        mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
        mesh.frustumCulled = true;
        mesh.userData = {
          defIndex,
          partIndex,
          panels: [],
          layer: part.layer,
          isGlass,
          // Instance status colours are multiplied by material.color — status
          // modes reset it to white so colours stay pure (see _applyColors).
          baseMaterialColor: material.color.clone(),
        };

        const matrix = new THREE.Matrix4();
        const baseColor = new THREE.Color(part.color || "#B0B7C3");
        list.forEach((inst, i) => {
          matrix.fromArray(inst.m);
          mesh.setMatrixAt(i, matrix);
          mesh.setColorAt(i, baseColor);
          mesh.userData.panels.push(inst.uid);
          if (!this.instanceIndex.has(inst.uid)) this.instanceIndex.set(inst.uid, []);
          this.instanceIndex.get(inst.uid).push({ mesh, index: i, base: baseColor.clone() });
          if (!this.panelMeta.has(inst.uid)) {
            this.panelMeta.set(inst.uid, { name: inst.name, matrix: inst.m, defIndex });
          }
        });
        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
        this.root.add(mesh);
        this.meshes.push(mesh);
      });
    });

    this.bbox.setFromObject(this.root);
    // Box3.setFromObject does not expand per instance, so derive the real model
    // bounds from the instance translations plus the largest definition radius.
    let radius = 0;
    this.meshes.forEach((mesh) => {
      const sphere = mesh.geometry.boundingSphere;
      if (sphere) radius = Math.max(radius, sphere.radius);
    });
    const point = new THREE.Vector3();
    const instanceBox = new THREE.Box3();
    this.panelMeta.forEach((meta) => {
      point.set(meta.matrix[12], meta.matrix[13], meta.matrix[14]);
      instanceBox.expandByPoint(point);
    });
    if (!instanceBox.isEmpty()) {
      instanceBox.expandByScalar(radius);
      this.bbox.union(instanceBox);
    }

    this.rebuildEdges();

    // Wait for the canvas to reach its final size before framing the model.
    requestAnimationFrame(() => {
      this.resize();
      this.fitToView();
    });
  }

  // ---------- light / detailed representations ----------

  /**
   * Light view: one oriented bounding box per panel, built from the block
   * definition's local bounds so position, rotation and size match the real
   * panel. No detailed geometry is downloaded or uploaded to the GPU.
   */
  buildLight(definitions, instances) {
    this._instances = instances;
    this._definitions = definitions;
    const groups = new Map();
    instances.forEach((inst) => {
      if (!groups.has(inst.def)) groups.set(inst.def, []);
      groups.get(inst.def).push(inst);
    });

    groups.forEach((list, defIndex) => {
      const definition = definitions[defIndex];
      // One box per structural part: corner panels keep their L silhouette and
      // each component keeps its own colour (PR / GL stay visually distinct).
      (definition?.partBoxes || []).forEach((boxDef, partIndex) => {
        let size;
        const placement = new THREE.Matrix4();
        if (boxDef.obb) {
          // Oriented box: axes[r*3+c] is component r of principal axis c.
          const a = boxDef.obb.axes;
          size = [...boxDef.obb.size];
          placement.makeBasis(
            new THREE.Vector3(a[0], a[3], a[6]),
            new THREE.Vector3(a[1], a[4], a[7]),
            new THREE.Vector3(a[2], a[5], a[8])
          );
          placement.setPosition(boxDef.obb.center[0], boxDef.obb.center[1], boxDef.obb.center[2]);
        } else {
          const { min, max } = boxDef;
          size = [0, 1, 2].map((ax) => Math.max(max[ax] - min[ax], 1e-6));
          placement.setPosition(
            (min[0] + max[0]) / 2,
            (min[1] + max[1]) / 2,
            (min[2] + max[2]) / 2
          );
        }
        // Adjacent panels share faces/edges in the real model; exact-size boxes
        // z-fight and read as one solid block. Inset the box around its own
        // centre: much thinner across the part, slightly shorter in-plane.
        const thin = size.indexOf(Math.min(...size));
        size[thin] *= 0.6;
        [0, 1, 2].forEach((ax) => {
          if (ax !== thin) size[ax] *= 0.97;
        });
        const geometry = new THREE.BoxGeometry(size[0], size[1], size[2]);
        geometry.applyMatrix4(placement);
        geometry.computeBoundingBox();
        geometry.computeBoundingSphere();

        const isGlass = /glass|cam/i.test(boxDef.layer || "");
        const material = new THREE.MeshLambertMaterial({
          color: new THREE.Color(boxDef.color || "#B0B7C3"),
          transparent: isGlass,
          opacity: isGlass ? 0.62 : 1,
          side: THREE.DoubleSide,
        });
        const mesh = new THREE.InstancedMesh(geometry, material, list.length);
        mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
        mesh.frustumCulled = true;
        mesh.userData = {
          defIndex,
          partIndex,
          panels: [],
          layer: boxDef.layer || null,
          isGlass,
          light: true,
          baseMaterialColor: material.color.clone(),
        };

        const matrix = new THREE.Matrix4();
        const baseColor = new THREE.Color(boxDef.color || "#B0B7C3");
        list.forEach((inst, i) => {
          matrix.fromArray(inst.m);
          mesh.setMatrixAt(i, matrix);
          mesh.setColorAt(i, baseColor);
          mesh.userData.panels.push(inst.uid);
          if (!this.lightIndex.has(inst.uid)) this.lightIndex.set(inst.uid, []);
          this.lightIndex.get(inst.uid).push({ mesh, index: i, base: baseColor.clone() });
          if (!this.panelMeta.has(inst.uid)) {
            this.panelMeta.set(inst.uid, { name: inst.name, matrix: inst.m, defIndex });
          }
        });
        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
        this.root.add(mesh);
        this.lightMeshes.push(mesh);
      });
    });

    this._syncActiveMeshes();
    this._computeModelBounds();
    this.rebuildEdges();
    requestAnimationFrame(() => {
      this.resize();
      this.fitToView();
    });
  }

  /** True when the panel is currently drawn with its detailed geometry. */
  _detailActive(uid) {
    return this.detailShown.has(uid) && this.detailIndex.has(uid);
  }

  /** Points meshes/instanceIndex at the representation that is actually drawn. */
  _syncActiveMeshes() {
    this.meshes = [...this.lightMeshes, ...this.detailMeshes];
    this.instanceIndex = new Map();
    this.panelMeta.forEach((_, uid) => {
      const entries =
        (this._detailActive(uid) ? this.detailIndex.get(uid) : this.lightIndex.get(uid)) || [];
      if (entries.length) this.instanceIndex.set(uid, entries);
    });
    this._mainPartCache = new Map();
  }

  /** Adds (hidden) detailed instances for one block definition. */
  addDetailDefinition(defIndex, parts) {
    if (this.detailLoaded.has(defIndex)) return;
    this.detailLoaded.add(defIndex);
    const list = (this._instances || []).filter((inst) => inst.def === defIndex);
    if (!list.length) return;

    parts.forEach((part, partIndex) => {
      const isGlass = /glass|cam/i.test(part.layer || "");
      const material = new THREE.MeshLambertMaterial({
        color: new THREE.Color(part.color || "#B0B7C3"),
        transparent: isGlass,
        opacity: isGlass ? 0.62 : 1,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.InstancedMesh(part.geometry, material, list.length);
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.frustumCulled = true;
      mesh.userData = {
        defIndex,
        partIndex,
        panels: [],
        layer: part.layer,
        isGlass,
        detail: true,
        baseMaterialColor: material.color.clone(),
      };
      const baseColor = new THREE.Color(part.color || "#B0B7C3");
      list.forEach((inst, i) => {
        // Detailed instances stay collapsed until the panel is switched over.
        mesh.setMatrixAt(i, ZERO_MATRIX);
        mesh.setColorAt(i, baseColor);
        mesh.userData.panels.push(inst.uid);
        if (!this.detailIndex.has(inst.uid)) this.detailIndex.set(inst.uid, []);
        this.detailIndex.get(inst.uid).push({ mesh, index: i, base: baseColor.clone() });
      });
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      try {
        if (!part.geometry.boundsTree) part.geometry.computeBoundsTree({ maxLeafTris: 12 });
      } catch (error) {
        console.debug("[viewer] BVH skipped for a detail mesh", error);
      }
      this.root.add(mesh);
      this.detailMeshes.push(mesh);
    });
  }

  /** Switches the given panels to detailed geometry; everything else stays light. */
  setDetailUids(uids) {
    this.detailShown = new Set(uids || []);
    this._refreshRepresentation();
  }

  /** Drops every detailed mesh and frees its CPU/GPU memory. */
  releaseDetail() {
    this.detailShown = new Set();
    this.detailMeshes.forEach((mesh) => {
      this.root.remove(mesh);
      this._edgeCache?.get(mesh.geometry.uuid)?.dispose?.();
      this._edgeCache?.delete(mesh.geometry.uuid);
      this._boundaryCache?.delete(mesh.geometry.uuid);
      mesh.geometry.disposeBoundsTree?.();
      mesh.geometry.dispose();
      mesh.material.dispose();
      mesh.dispose?.();
    });
    this.detailMeshes = [];
    this.detailIndex = new Map();
    this.detailLoaded = new Set();
    this._refreshRepresentation();
  }

  _refreshRepresentation() {
    // Legacy full-build path has no light representation — nothing to switch.
    if (!this.lightMeshes.length) return;
    this._syncActiveMeshes();
    this.panelMeta.forEach((_, uid) => this._setInstanceVisible(uid, !this.hidden.has(uid)));
    // Detail instances are created collapsed (zero matrix) and can be
    // frustum-culled/raycast before they are shown — caching a degenerate
    // bounding sphere at the origin. The panel then disappears and becomes
    // unselectable when zoomed away from the origin while its edges (drawn
    // into the unculled overlay group) stay visible. Invalidate the cached
    // volumes so three.js recomputes them with the matrices just written.
    this.meshes.forEach((mesh) => {
      mesh.boundingSphere = null;
      mesh.boundingBox = null;
    });
    if (this.clipPlane) {
      this.meshes.forEach((mesh) => {
        mesh.material.clippingPlanes = [this.clipPlane];
        mesh.material.needsUpdate = true;
      });
    }
    // Force the material pass in _applyColors: freshly added meshes need it.
    this._ghosting = null;
    this._opaqueMode = null;
    this._applyColors();
    this.rebuildEdges();
    this._buildSelectionOverlay();
    if (this._labelItems) this.setPanelLabels(this._labelItems);
    this._markDirty();
  }

  /** Real model bounds: instance translations expanded by the largest definition radius. */
  _computeModelBounds() {
    this.bbox.setFromObject(this.root);
    let radius = 0;
    this.meshes.forEach((mesh) => {
      const sphere = mesh.geometry.boundingSphere;
      if (sphere) radius = Math.max(radius, sphere.radius);
    });
    const point = new THREE.Vector3();
    const instanceBox = new THREE.Box3();
    this.panelMeta.forEach((meta) => {
      point.set(meta.matrix[12], meta.matrix[13], meta.matrix[14]);
      instanceBox.expandByPoint(point);
    });
    if (!instanceBox.isEmpty()) {
      instanceBox.expandByScalar(radius);
      this.bbox.union(instanceBox);
    }
  }

  _badgeTexture(kind) {
    if (this._badgeTextures[kind]) return this._badgeTextures[kind];
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 128;
    const ctx = canvas.getContext("2d");
    if (kind === "note") {
      // Small blue square with white note lines.
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(66, 6, 124, 116);
      ctx.fillStyle = "#2563EB";
      ctx.fillRect(72, 12, 112, 104);
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(90, 40, 76, 9);
      ctx.fillRect(90, 60, 76, 9);
      ctx.fillRect(90, 80, 46, 9);
    } else {
      ctx.fillStyle = "#15803D";
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(28, 34, 200, 60, 14);
        ctx.fill();
      } else {
        ctx.fillRect(28, 34, 200, 60);
      }
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "bold 44px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("NEW", 128, 66);
    }
    this._badgeTextures[kind] = new THREE.CanvasTexture(canvas);
    return this._badgeTextures[kind];
  }

  _badgeSprite(kind, uid, position, scale) {
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this._badgeTexture(kind),
        depthTest: false,
        transparent: true,
        sizeAttenuation: false,
      })
    );
    sprite.position.copy(position);
    sprite.scale.set(scale[0], scale[1], 1);
    sprite.renderOrder = 998;
    sprite.userData.panelUid = uid;
    return sprite;
  }

  panelCenter(uid) {
    const entries = this.instanceIndex.get(uid) || [];
    if (!entries.length) return null;
    const matrix = new THREE.Matrix4();
    const box = new THREE.Box3();
    entries.forEach(({ mesh, index }) => {
      mesh.getMatrixAt(index, matrix);
      const geoBox = mesh.geometry.boundingBox?.clone();
      if (!geoBox) return;
      geoBox.applyMatrix4(matrix);
      box.union(geoBox);
    });
    return box.isEmpty() ? null : box.getCenter(new THREE.Vector3());
  }

  /** Panel bounding box in world space (null when the panel is not in the model). */
  panelBox(uid) {
    const entries = this.instanceIndex.get(uid) || [];
    if (!entries.length) return null;
    const matrix = new THREE.Matrix4();
    const box = new THREE.Box3();
    entries.forEach(({ mesh, index }) => {
      mesh.getMatrixAt(index, matrix);
      const geoBox = mesh.geometry.boundingBox?.clone();
      if (!geoBox) return;
      geoBox.applyMatrix4(matrix);
      box.union(geoBox);
    });
    return box.isEmpty() ? null : box;
  }


  /**
   * Block definition code + unique code on each panel. Sizing is derived from the panel's
   * MAIN surface measured in its own plane (not the axis-aligned bounding box), so slanted
   * or narrow panels keep the text inside their own outline. A single median-based target
   * width keeps all labels visually consistent instead of huge on big panels and tiny on
   * small ones.
   */
  setPanelLabels(items) {
    this.labelGroup.clear();
    // Remembered so labels can be re-sized when a panel switches representation.
    this._labelItems = items?.length ? items : null;
    if (!items?.length) return;
    this._labelTextures = this._labelTextures || {};

    const matrix = new THREE.Matrix4();
    const specs = [];
    items.forEach(({ uid, text, subtext }) => {
      if (!text) return;
      const entry = this._mainPartFor(uid);
      if (!entry) return;
      const geometry = entry.mesh.geometry;
      if (!geometry.boundingBox) geometry.computeBoundingBox();
      entry.mesh.getMatrixAt(entry.index, matrix);
      const scale = new THREE.Vector3().setFromMatrixScale(matrix);
      const local = geometry.boundingBox.getSize(new THREE.Vector3());
      const dims = [local.x * scale.x, local.y * scale.y, local.z * scale.z];
      const thin = dims.indexOf(Math.min(...dims));
      const plane = dims.filter((_, i) => i !== thin);
      const short = Math.min(...plane);
      const long = Math.max(...plane);
      if (!(short > 0) || !(long > 0)) return;
      const center = geometry.boundingBox
        .getCenter(new THREE.Vector3())
        .applyMatrix4(matrix);
      specs.push({ uid, text, subtext, short, long, center });
    });
    if (!specs.length) return;

    const shorts = specs.map((s) => s.short).sort((a, b) => a - b);
    const median = shorts[Math.floor(shorts.length / 2)];
    const target = median * 0.78;

    specs.forEach(({ uid, text, subtext, short, long, center }) => {
      const texture = this._labelTexture([text, subtext]);
      const aspect = texture.image.width / texture.image.height;
      // Never wider than the panel's narrow side, never taller than a third of its length.
      let width = Math.min(target, short * 0.86, long * 0.34 * aspect);
      if (!(width > 0)) return;
      const height = width / aspect;

      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: texture,
          transparent: true,
          depthWrite: false,
          depthTest: false,
        })
      );
      sprite.position.copy(center);
      sprite.scale.set(width, height, 1);
      sprite.renderOrder = 1000;
      sprite.userData.panelUid = uid;
      this.labelGroup.add(sprite);
    });
    this._syncLabelVisibility();
  }

  _labelTexture(lines) {
    const rows = (Array.isArray(lines) ? lines : [lines]).filter(Boolean);
    const key = rows.join("\n");
    if (this._labelTextures[key]) return this._labelTextures[key];
    const canvas = document.createElement("canvas");
    const font = 64;
    const sub = 48;
    const pad = 14;
    const gap = 10;
    const measure = document.createElement("canvas").getContext("2d");
    const widths = rows.map((row, index) => {
      measure.font = `bold ${index ? sub : font}px monospace`;
      return measure.measureText(row).width;
    });
    canvas.width = Math.ceil(Math.max(...widths)) + pad * 2;
    canvas.height =
      font + pad * 2 + (rows.length > 1 ? (rows.length - 1) * (sub + gap) : 0);
    const ctx = canvas.getContext("2d");
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineWidth = 8;
    let y = pad + font / 2;
    rows.forEach((row, index) => {
      ctx.font = `bold ${index ? sub : font}px monospace`;
      ctx.strokeStyle = "rgba(255,255,255,0.92)";
      ctx.strokeText(row, canvas.width / 2, y);
      ctx.fillStyle = index ? "#0891B2" : "#0B1120";
      ctx.fillText(row, canvas.width / 2, y);
      y += (index ? sub : font) / 2 + gap + sub / 2;
    });
    const texture = new THREE.CanvasTexture(canvas);
    this._labelTextures[key] = texture;
    return texture;
  }

  /** Small yellow note badge in the panel's upper-right corner (with a count when >1). */
  setNoteMarkers(items) {
    this.noteGroup.clear();
    this._blinkers = (this._blinkers || []).filter((s) => s.parent);
    (items || []).forEach((item) => {
      const uid = typeof item === "string" ? item : item.uid;
      const count = typeof item === "string" ? 1 : item.count || 1;
      const box = this.panelBox(uid);
      if (!box) return;
      const center = box.getCenter(new THREE.Vector3());
      // Top centre of the panel, nudged down slightly so the badge stays on it.
      const corner = new THREE.Vector3(center.x, center.y, box.max.z).lerp(center, 0.16);
      const wide = count > 1;
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: this._noteBadgeTexture(count),
          depthTest: false,
          transparent: true,
          sizeAttenuation: false,
        })
      );
      sprite.position.copy(corner);
      sprite.scale.set(wide ? 0.034 : 0.022, 0.022, 1);
      sprite.renderOrder = 998;
      sprite.userData.panelUid = uid;
      if (typeof item !== "string" && item.recent) this._blinkers.push(sprite);
      this.noteGroup.add(sprite);
    });
    this._markDirty();
  }

  /** Yellow sticky-note glyph, optionally followed by the note count. */
  _noteBadgeTexture(count) {
    this._noteTextures = this._noteTextures || {};
    const key = count > 1 ? String(count) : "1";
    if (this._noteTextures[key]) return this._noteTextures[key];
    const wide = count > 1;
    const canvas = document.createElement("canvas");
    canvas.width = wide ? 96 : 64;
    canvas.height = 64;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FACC15";
    ctx.strokeStyle = "#A16207";
    ctx.lineWidth = 4;
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(6, 10, 44, 44, 8);
    else ctx.rect(6, 10, 44, 44);
    ctx.fill();
    ctx.stroke();
    ctx.strokeStyle = "#854D0E";
    ctx.lineWidth = 4;
    [24, 33, 42].forEach((y) => {
      ctx.beginPath();
      ctx.moveTo(15, y);
      ctx.lineTo(y === 42 ? 33 : 41, y);
      ctx.stroke();
    });
    if (wide) {
      ctx.font = "bold 40px monospace";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.lineWidth = 6;
      ctx.strokeStyle = "rgba(255,255,255,0.95)";
      ctx.strokeText(String(count), 56, 33);
      ctx.fillStyle = "#854D0E";
      ctx.fillText(String(count), 56, 33);
    }
    const texture = new THREE.CanvasTexture(canvas);
    this._noteTextures[key] = texture;
    return texture;
  }

  /** Green NEW badges for panels added in the latest revision. */
  setNewMarkers(uids) {
    this.newGroup.clear();
    (uids || []).forEach((uid) => {
      const center = this.panelCenter(uid);
      if (!center) return;
      this.newGroup.add(this._badgeSprite("new", uid, center, [0.07, 0.035]));
    });
    this._markDirty();
  }

  /** Hovering a NEW badge dismisses it permanently for the current user. */
  _hoverNewBadge(event) {
    if (!this.newGroup.children.length) return;
    const uid = this._pickSprite(event, this.newGroup, 26);
    if (!uid) return;
    const sprite = this.newGroup.children.find((s) => s.userData.panelUid === uid);
    if (sprite) this.newGroup.remove(sprite);
    this.onNewBadgeSeen?.(uid);
    this._markDirty();
  }

  /** Colour-coded "!" badges per issue type; NCR panels get a red marker. */
  setIssueMarkers(items) {
    this.issueGroup.clear();
    this._blinkers = (this._blinkers || []).filter((s) => s.parent);
    if (!items?.length) return;
    this._issueTextures = this._issueTextures || {};

    const size = 0.032;
    const matrix = new THREE.Matrix4();
    const center = new THREE.Vector3();
    const box = new THREE.Box3();

    items.forEach((item) => {
      const uid = typeof item === "string" ? item : item.uid;
      const color = (typeof item === "string" ? null : item.color) || "#F97316";
      const entries = this.instanceIndex.get(uid) || [];
      if (!entries.length) return;
      box.makeEmpty();
      entries.forEach(({ mesh, index }) => {
        mesh.getMatrixAt(index, matrix);
        const geoBox = mesh.geometry.boundingBox?.clone();
        if (!geoBox) return;
        geoBox.applyMatrix4(matrix);
        box.union(geoBox);
      });
      if (box.isEmpty()) return;
      box.getCenter(center);
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: this._issueBadgeTexture(color),
          depthTest: false,
          transparent: true,
          sizeAttenuation: false,
        })
      );
      sprite.position.copy(center);
      sprite.scale.set(size, size, size);
      sprite.renderOrder = 999;
      sprite.userData.panelUid = uid;
      if (typeof item !== "string" && item.recent) this._blinkers.push(sprite);
      this.issueGroup.add(sprite);
    });
    this._markDirty();
  }

  _issueBadgeTexture(color) {
    if (this._issueTextures[color]) return this._issueTextures[color];
    const canvas = document.createElement("canvas");
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.arc(64, 64, 56, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 8;
    ctx.strokeStyle = "#FFFFFF";
    ctx.stroke();
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "bold 82px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("!", 64, 66);
    this._issueTextures[color] = new THREE.CanvasTexture(canvas);
    return this._issueTextures[color];
  }

  fitToView(uids = null) {
    const box = new THREE.Box3();
    if (uids && uids.length) {
      const matrix = new THREE.Matrix4();
      uids.forEach((uid) => {
        (this.instanceIndex.get(uid) || []).forEach(({ mesh, index }) => {
          mesh.getMatrixAt(index, matrix);
          const geometryBox = mesh.geometry.boundingBox
            ? mesh.geometry.boundingBox.clone()
            : new THREE.Box3().setFromBufferAttribute(mesh.geometry.attributes.position);
          box.union(geometryBox.applyMatrix4(matrix));
        });
      });
    } else {
      box.copy(this.bbox);
    }
    if (box.isEmpty()) return;
    const center = box.getCenter(new THREE.Vector3());
    const radius = box.getBoundingSphere(new THREE.Sphere()).radius || 10;
    const fov = (this.camera.fov * Math.PI) / 180;
    const distance = (radius / Math.sin(fov / 2)) * 1.15;
    this.controls.target.copy(center);
    const direction = new THREE.Vector3(0.75, -1, 0.55).normalize();
    this.camera.position.copy(center.clone().addScaledVector(direction, distance));
    this.camera.near = Math.max(radius / 5000, 0.05);
    this.camera.far = distance * 20;
    this.camera.updateProjectionMatrix();
    this.controls.update();
    this._markDirty();
  }

  homeView() {
    this.fitToView();
  }

  setMode(mode) {
    this.mode = mode;
    this.controls.enableRotate = mode !== "pan" && mode !== "box";
    this.controls.enablePan = true;
    this.controls.enabled = true;
    if (mode !== "measure") this.clearMeasure();
  }

  // ---------- selection ----------

  _handlePointerDown(event) {
    this._camTween = null;
    this._downPos = { x: event.clientX, y: event.clientY };
    if (this.mode === "box") {
      const rect = this.renderer.domElement.getBoundingClientRect();
      this._boxStart = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      this.controls.enabled = false;
      this.boxEl.style.display = "block";
      this._drawBox(this._boxStart, this._boxStart);
    }
  }

  _drawBox(a, b) {
    const left = Math.min(a.x, b.x);
    const top = Math.min(a.y, b.y);
    this.boxEl.style.left = `${left}px`;
    this.boxEl.style.top = `${top}px`;
    this.boxEl.style.width = `${Math.abs(a.x - b.x)}px`;
    this.boxEl.style.height = `${Math.abs(a.y - b.y)}px`;
  }

  _handlePointerMove(event) {
    this._hoverNewBadge(event);
    if (!this._boxStart) return;
    const rect = this.renderer.domElement.getBoundingClientRect();
    this._drawBox(this._boxStart, {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  }

  /** Selects every visible panel whose centre projects inside the drag box. */
  _finishBoxSelect(event, additive) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const end = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    const start = this._boxStart;
    this._boxStart = null;
    this.boxEl.style.display = "none";
    this.controls.enabled = true;

    if (Math.abs(start.x - end.x) < 4 && Math.abs(start.y - end.y) < 4) {
      // A plain click inside box-select mode: pick the panel under the cursor,
      // or clear the selection when the click lands on empty space.
      const hit = this._pick(event);
      const uid = hit ? hit.mesh.userData.panels[hit.instanceId] : null;
      if (!uid) {
        if (!additive) this.setSelection([]);
        return;
      }
      if (this.locked.has(uid)) return;
      const next = additive ? new Set(this.selection) : new Set();
      if (additive && next.has(uid)) next.delete(uid);
      else next.add(uid);
      this.setSelection([...next]);
      return;
    }
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    const point = new THREE.Vector3();
    const hits = [];
    this.panelMeta.forEach((meta, uid) => {
      if (this.hidden.has(uid) || this.locked.has(uid)) return;
      point.set(meta.matrix[12], meta.matrix[13], meta.matrix[14]);
      point.project(this.camera);
      if (point.z < -1 || point.z > 1) return;
      const sx = ((point.x + 1) / 2) * rect.width;
      const sy = ((1 - point.y) / 2) * rect.height;
      if (sx >= minX && sx <= maxX && sy >= minY && sy <= maxY) hits.push(uid);
    });

    const next = additive ? new Set([...this.selection, ...hits]) : new Set(hits);
    this.setSelection([...next]);
  }

  _handlePointerUp(event) {
    if (this._boxStart) {
      this._finishBoxSelect(event, event.shiftKey || event.ctrlKey || event.metaKey);
      this._downPos = null;
      return;
    }
    if (!this._downPos) return;
    const moved =
      Math.abs(event.clientX - this._downPos.x) + Math.abs(event.clientY - this._downPos.y);
    this._downPos = null;
    if (moved > 4) return;

    // Shift/Ctrl-click always means "extend the selection", never "open the issue popup".
    const additive = event.shiftKey || event.ctrlKey || event.metaKey;
    const markerHit = additive ? null : this._pickIssueMarker(event);
    if (markerHit && this.onIssueMarkerClick) {
      this.onIssueMarkerClick(markerHit);
      return;
    }

    const hit = this._pick(event);
    if (this.mode === "measure") {
      if (hit) this._addMeasurePoint(this.snapToVertex(hit));
      return;
    }
    if (!hit) {
      if (!event.shiftKey) this.setSelection([]);
      return;
    }
    const uid = hit.mesh.userData.panels[hit.instanceId];
    if (!uid || this.locked.has(uid)) return;
    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      const next = new Set(this.selection);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      this.setSelection([...next]);
    } else {
      this.setSelection([uid]);
    }
  }

  _pickIssueMarker(event) {
    return this._pickSprite(event, this.issueGroup, 20);
  }

  _pickSprite(event, group, radius) {
    if (!group.children.length) return null;
    const rect = this.renderer.domElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const projected = new THREE.Vector3();
    let best = null;
    let bestDist = radius;
    group.children.forEach((sprite) => {
      projected.copy(sprite.position).project(this.camera);
      if (projected.z < -1 || projected.z > 1) return;
      const sx = ((projected.x + 1) / 2) * rect.width;
      const sy = ((1 - projected.y) / 2) * rect.height;
      const dist = Math.hypot(sx - x, sy - y);
      if (dist < bestDist) {
        bestDist = dist;
        best = sprite.userData.panelUid;
      }
    });
    return best;
  }

  _pick(event) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const pointer = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
    this.raycaster.setFromCamera(pointer, this.camera);
    const hits = this.raycaster.intersectObjects(this.meshes, false);
    const hit = hits.find((h) => h.instanceId !== undefined);
    if (!hit) return null;
    return { mesh: hit.object, instanceId: hit.instanceId, point: hit.point };
  }

  setSelection(uids, notify = true) {
    this.selection = new Set(uids.filter((uid) => !this.locked.has(uid)));
    this._markDirty();
    this._applyColors();
    this._buildSelectionOverlay();
    if (notify) this.onSelectionChange?.([...this.selection]);
  }

  /** Rebuilds the vivid overlay copies of the selected instances. */
  _buildSelectionOverlay() {
    // Overlay meshes share the InstancedMesh geometry, so they must never dispose it.
    this.selectionGroup.children.forEach((child) => {
      if (child.isLineSegments2) child.geometry.dispose();
    });
    this.selectionGroup.clear();
    if (!this.selection.size) return;
    this._highlightMaterial =
      this._highlightMaterial ||
      new THREE.MeshBasicMaterial({
        color: HIGHLIGHT,
        depthWrite: true,
        // Both faces so a panel seen from behind stays just as vivid, and a small
        // polygon offset so the overlay always wins against the base geometry.
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      });
    // Thin bright outline so panel borders stay readable in a dense facade.
    const matrix = new THREE.Matrix4();
    this.selection.forEach((uid) => {
      if (this.hidden.has(uid)) return;
      (this.instanceIndex.get(uid) || []).forEach(({ mesh, index }) => {
        mesh.getMatrixAt(index, matrix);
        const overlay = new THREE.Mesh(mesh.geometry, this._highlightMaterial);
        overlay.applyMatrix4(matrix);
        overlay.renderOrder = 400;
        this.selectionGroup.add(overlay);
      });
    });

    // Selected panels keep their inner black layer edges; the outer boundary turns white.
    const selected = (uid) => this.selection.has(uid);
    const { inner } = this._collectEdges(selected);
    const outer = [];
    this.selection.forEach((uid) => {
      if (!this.hidden.has(uid)) this._pushPanelOutline(uid, outer);
    });
    const innerLine = this._makeEdgeLine(inner, this._selInnerMaterial, 402);
    if (innerLine) this.selectionGroup.add(innerLine);
    const outerLine = this._makeEdgeLine(outer, this._selOuterMaterial, 403);
    if (outerLine) this.selectionGroup.add(outerLine);
  }

  /** Persistent thin black edges around every geometry / layer inside the panels. */
  rebuildEdges() {
    this.edgeGroup.children.forEach((child) => child.geometry.dispose());
    this.edgeGroup.clear();
    const { inner } = this._collectEdges(() => true);
    const innerLine = this._makeEdgeLine(inner, this._innerEdgeMaterial, 2);
    if (innerLine) this.edgeGroup.add(innerLine);

    // Panel boundary outlines for every visible panel — shown INSTEAD of the dense
    // inner edges when the camera is far away (see _syncEdgeZoom).
    if (!this.boundaryGroup) {
      this.boundaryGroup = new THREE.Group();
      this.scene.add(this.boundaryGroup);
    }
    this.boundaryGroup.children.forEach((child) => child.geometry.dispose());
    this.boundaryGroup.clear();
    const boundary = [];
    this.panelMeta.forEach((_, uid) => {
      if (!this.hidden.has(uid)) this._pushPanelOutline(uid, boundary);
    });
    const boundaryLine = this._makeEdgeLine(boundary, this._innerEdgeMaterial, 2);
    if (boundaryLine) this.boundaryGroup.add(boundaryLine);
    this._syncEdgeZoom();
  }

  _makeEdgeLine(positions, material, renderOrder) {
    if (!positions.length) return null;
    const geometry = new LineSegmentsGeometry();
    geometry.setPositions(positions);
    const line = new LineSegments2(geometry, material);
    line.frustumCulled = false;
    line.renderOrder = renderOrder;
    return line;
  }

  /** Flat [x,y,z,...] segment pairs for every visible geometry part; `outer` only for selection. */
  _collectEdges(includeUid, withOuter = false) {
    const inner = [];
    const outer = [];
    const matrix = new THREE.Matrix4();
    const vertex = new THREE.Vector3();
    this.meshes.forEach((mesh) => {
      if (!mesh.visible) return;
      const isDetailMesh = !!mesh.userData.detail;
      const positions = this._edgesFor(mesh.geometry).attributes.position.array;
      for (let i = 0; i < mesh.count; i += 1) {
        const uid = mesh.userData.panels[i];
        if (this.hidden.has(uid) || !includeUid(uid)) continue;
        // Skip the representation that is currently collapsed for this panel.
        if (this.lightMeshes.length && isDetailMesh !== this._detailActive(uid)) continue;
        mesh.getMatrixAt(i, matrix);
        for (let k = 0; k < positions.length; k += 3) {
          vertex.set(positions[k], positions[k + 1], positions[k + 2]).applyMatrix4(matrix);
          inner.push(vertex.x, vertex.y, vertex.z);
        }
      }
    });
    this.panelMeta.forEach((_, uid) => {
      if (!withOuter || this.hidden.has(uid) || !includeUid(uid)) return;
      this._pushPanelOutline(uid, outer);
    });
    return { inner, outer };
  }

  /** The panel's outer contour: boundary edges of its main (largest) surface. */
  _pushPanelOutline(uid, out) {
    const entry = this._mainPartFor(uid);
    if (!entry) return;
    const positions = this._boundaryEdgesFor(entry.mesh.geometry);
    if (!positions.length) return;
    const matrix = new THREE.Matrix4();
    entry.mesh.getMatrixAt(entry.index, matrix);
    const vertex = new THREE.Vector3();
    for (let k = 0; k < positions.length; k += 3) {
      vertex.set(positions[k], positions[k + 1], positions[k + 2]).applyMatrix4(matrix);
      out.push(vertex.x, vertex.y, vertex.z);
    }
  }

  /** Largest visible surface of a panel — the face that is highlighted on selection. */
  _mainPartFor(uid) {
    this._mainPartCache = this._mainPartCache || new Map();
    const entries = (this.instanceIndex.get(uid) || []).filter((e) => e.mesh.visible);
    if (!entries.length) return null;
    const cached = this._mainPartCache.get(uid);
    if (cached && cached.mesh.visible) return cached;
    let best = null;
    let bestArea = -1;
    entries.forEach((entry) => {
      const geometry = entry.mesh.geometry;
      if (!geometry.boundingBox) geometry.computeBoundingBox();
      const size = geometry.boundingBox.getSize(new THREE.Vector3());
      const dims = [size.x, size.y, size.z].sort((a, b) => b - a);
      const area = dims[0] * dims[1];
      if (area > bestArea) {
        bestArea = area;
        best = entry;
      }
    });
    this._mainPartCache.set(uid, best);
    return best;
  }

  /**
   * Open boundary edges of a geometry (edges used by a single triangle) — the real
   * outer contour of the surface, unlike a bounding box.
   */
  _boundaryEdgesFor(geometry) {
    this._boundaryCache = this._boundaryCache || new Map();
    const hit = this._boundaryCache.get(geometry.uuid);
    if (hit) return hit;
    const position = geometry.attributes.position;
    const index = geometry.index;
    const count = index ? index.count : position.count;
    const key = (i) => {
      const v = index ? index.getX(i) : i;
      // Quantise so shared vertices duplicated by the exporter still match.
      return `${Math.round(position.getX(v) * 1e4)},${Math.round(position.getY(v) * 1e4)},${Math.round(
        position.getZ(v) * 1e4
      )}`;
    };
    const edges = new Map();
    for (let t = 0; t < count; t += 3) {
      for (let e = 0; e < 3; e += 1) {
        const a = key(t + e);
        const b = key(t + ((e + 1) % 3));
        const id = a < b ? `${a}|${b}` : `${b}|${a}`;
        const existing = edges.get(id);
        if (existing) existing.n += 1;
        else edges.set(id, { n: 1, a: t + e, b: t + ((e + 1) % 3) });
      }
    }
    const out = [];
    edges.forEach(({ n, a, b }) => {
      if (n !== 1) return;
      [a, b].forEach((i) => {
        const v = index ? index.getX(i) : i;
        out.push(position.getX(v), position.getY(v), position.getZ(v));
      });
    });
    // A closed solid (e.g. a light bounding box) has no open boundary edge —
    // fall back to its full edge set so panel outlines still exist.
    const result = new Float32Array(
      out.length ? out : this._edgesFor(geometry).attributes.position.array
    );
    this._boundaryCache.set(geometry.uuid, result);
    return result;
  }

  /** Cached edge geometry per shared panel geometry — outlines are cheap to reuse. */
  _edgesFor(geometry) {
    this._edgeCache = this._edgeCache || new Map();
    let edges = this._edgeCache.get(geometry.uuid);
    if (!edges) {
      edges = new THREE.EdgesGeometry(geometry, 20);
      this._edgeCache.set(geometry.uuid, edges);
    }
    return edges;
  }

  // ---------- visibility ----------

  _setInstanceVisible(uid, visible) {
    const meta = this.panelMeta.get(uid);
    if (!meta) return;
    const matrix = new THREE.Matrix4().fromArray(meta.matrix);
    const apply = (entries, active) =>
      (entries || []).forEach((entry) => {
        entry.mesh.setMatrixAt(entry.index, visible && active ? matrix : ZERO_MATRIX);
        entry.mesh.instanceMatrix.needsUpdate = true;
      });
    if (this.lightMeshes.length) {
      // Light-first model: exactly one representation of the panel is drawn.
      const detail = this._detailActive(uid);
      apply(this.lightIndex.get(uid), !detail);
      apply(this.detailIndex.get(uid), detail);
      return;
    }
    apply(this.instanceIndex.get(uid), true);
  }

  hide(uids) {
    uids.forEach((uid) => {
      this.hidden.add(uid);
      this._setInstanceVisible(uid, false);
    });
    this.rebuildEdges();
    this._buildSelectionOverlay();
    this._syncLabelVisibility();
    this._markDirty();
  }

  isolate(uids) {
    const keep = new Set(uids);
    this.panelMeta.forEach((_, uid) => {
      const visible = keep.has(uid);
      if (visible) this.hidden.delete(uid);
      else this.hidden.add(uid);
      this._setInstanceVisible(uid, visible);
    });
    this.rebuildEdges();
    this._buildSelectionOverlay();
    this._syncLabelVisibility();
    this._markDirty();
  }

  hideOthers(uids) {
    this.isolate(uids);
  }

  showAll() {
    this.hidden.forEach((uid) => this._setInstanceVisible(uid, true));
    this.hidden.clear();
    this.rebuildEdges();
    this._buildSelectionOverlay();
    this._syncLabelVisibility();
    this._markDirty();
  }

  /**
   * Labels follow panel visibility and the active section plane. Every visible panel
   * keeps its code at any camera angle (no back-face culling).
   */
  _syncLabelVisibility() {
    this.labelGroup.children.forEach((sprite) => {
      const hiddenPanel = this.hidden.has(sprite.userData.panelUid);
      const clipped =
        this.clipPlane != null && this.clipPlane.distanceToPoint(sprite.position) < 0;
      sprite.visible = !hiddenPanel && !clipped;
    });
  }

  setLayerVisible(layer, visible) {
    this.meshes
      .filter((mesh) => mesh.userData.layer === layer)
      .forEach((mesh) => {
        mesh.visible = visible;
      });
    this.rebuildEdges();
    this._buildSelectionOverlay();
    this._markDirty();
  }

  // ---------- colouring ----------

  setStatusColors(map) {
    this.statusColors = map;
    this._applyColors();
    this._markDirty();
  }

  /** Revision-comparison overlay: panelUid -> category colour. */
  setCompareColors(map) {
    this.compareColors = map || new Map();
    this._applyColors();
    this._markDirty();
  }

  /** Non-selectable panels shown in muted grey (Factory eligibility gate). */
  setLocked(uids) {
    this.locked = new Set(uids || []);
    if (this.selection.size) {
      const next = [...this.selection].filter((uid) => !this.locked.has(uid));
      if (next.length !== this.selection.size) this.setSelection(next);
    }
    this._applyColors();
    this._markDirty();
  }

  setColorMode(mode) {
    this.colorMode = mode;
    this._applyColors();
    this._markDirty();
  }

  /** Build a bounds tree per shared geometry (once) — raycasts skip whole branches. */
  buildAcceleration() {
    this.meshes.forEach((mesh) => {
      mesh.frustumCulled = true;
      if (!mesh.geometry.boundsTree) {
        try {
          mesh.geometry.computeBoundsTree({ maxLeafTris: 12 });
        } catch (error) {
          // Geometry without index/position — keep the default raycast.
          console.debug("[viewer] BVH skipped for a mesh", error);
        }
      }
      mesh.computeBoundingSphere?.();
    });
  }

  _applyColors() {
    const color = new THREE.Color();
    const touched = new Set();
    const compareMode = this.colorMode === "compare";
    const statusMode = this.colorMode !== "original" && !compareMode;
    const opaque = statusMode || compareMode;
    // With an active selection everything else fades to a colourless ghost
    // (unless ghostUnselected is disabled, e.g. in the Factory workspace).
    const ghosting = this.ghostUnselected && this.selection.size > 0;
    // Material flags force a shader/state refresh, so only touch them when the
    // ghost state actually flips — recolouring alone must stay attribute-only.
    if (ghosting !== this._ghosting || opaque !== this._opaqueMode) {
      this._ghosting = ghosting;
      this._opaqueMode = opaque;
      // Unselected panels ghost out, so their edges must fade with them.
      this._innerEdgeMaterial.opacity = ghosting ? 0.08 : 0.55;
      this.meshes.forEach((mesh) => {
        const transparent = ghosting || (mesh.userData.isGlass && !opaque);
        let opacity = 1;
        if (ghosting) opacity = 0.16;
        else if (transparent) opacity = 0.62;
        // Pure status colours: white material base in status/compare modes,
        // the part's own colour in original mode.
        mesh.material.color.copy(
          opaque ? WHITE : mesh.userData.baseMaterialColor
        );
        mesh.material.transparent = transparent;
        mesh.material.opacity = opacity;
        mesh.material.depthWrite = !ghosting;
        mesh.material.needsUpdate = true;
      });
    }
    this.instanceIndex.forEach((entries, uid) => {
      const selected = this.selection.has(uid);
      let target;
      if (selected) target = HIGHLIGHT;
      else if (this.locked.has(uid)) target = color.set("#47505C");
      else if (ghosting) target = color.set("#94A3B8");
      else if (compareMode) {
        const hex = this.compareColors.get(uid);
        target = color.set(hex || "#CBD5E1");
      } else if (!statusMode) target = null;
      else {
        const hex = this.statusColors.get(uid);
        // Explicit null keeps the panel's original appearance (e.g. Factory:
        // Awaiting Cutting has no status colour); a missing entry falls back to grey.
        target = hex === null ? null : hex ? color.set(hex) : new THREE.Color("#64748B");
      }
      entries.forEach((entry) => {
        entry.mesh.setColorAt(entry.index, target ? target : entry.base);
        touched.add(entry.mesh);
      });
    });
    touched.forEach((mesh) => {
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    });
  }

  // ---------- section plane ----------

  setSection(axis, value) {
    if (!axis) {
      this.clipPlane = null;
      this.meshes.forEach((mesh) => {
        mesh.material.clippingPlanes = [];
      });
      this._syncLabelVisibility();
      this._markDirty();
      return;
    }
    const size = this.bbox.getSize(new THREE.Vector3());
    const min = this.bbox.min;
    const normals = { x: new THREE.Vector3(-1, 0, 0), y: new THREE.Vector3(0, -1, 0), z: new THREE.Vector3(0, 0, -1) };
    const extent = { x: size.x, y: size.y, z: size.z }[axis];
    const origin = { x: min.x, y: min.y, z: min.z }[axis];
    const constant = origin + extent * value;
    this.clipPlane = new THREE.Plane(normals[axis], constant);
    this.meshes.forEach((mesh) => {
      mesh.material.clippingPlanes = [this.clipPlane];
      mesh.material.needsUpdate = true;
    });
    this._syncLabelVisibility();
    this._markDirty();
  }

  // ---------- measurement ----------

  /** OSNAP: snap a raw hit to the nearest triangle vertex (panel corner) of that instance. */
  snapToVertex(hit) {
    if (!hit?.object?.geometry || hit.instanceId == null) return hit.point.clone();
    const mesh = hit.object;
    const position = mesh.geometry.attributes.position;
    const matrix = new THREE.Matrix4();
    mesh.getMatrixAt(hit.instanceId, matrix);
    const vertex = new THREE.Vector3();
    const best = hit.point.clone();
    let bestDist = this.bbox.getSize(new THREE.Vector3()).length() * 0.02;
    for (let i = 0; i < position.count; i += 1) {
      vertex.fromBufferAttribute(position, i).applyMatrix4(matrix);
      const dist = vertex.distanceTo(hit.point);
      if (dist < bestDist) {
        bestDist = dist;
        best.copy(vertex);
      }
    }
    return best;
  }

  _measureLabel(text, position) {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 128;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0E7490";
    if (ctx.roundRect) {
      ctx.beginPath();
      ctx.roundRect(6, 24, 500, 80, 18);
      ctx.fill();
    } else {
      ctx.fillRect(6, 24, 500, 80);
    }
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "bold 54px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, 256, 66);
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: new THREE.CanvasTexture(canvas),
        depthTest: false,
        transparent: true,
        sizeAttenuation: false,
      })
    );
    sprite.position.copy(position);
    sprite.scale.set(0.16, 0.04, 1);
    sprite.renderOrder = 1000;
    return sprite;
  }

  _addMeasurePoint(point) {
    this.measurePoints.push(point.clone());
    const dot = new THREE.Mesh(
      new THREE.SphereGeometry(this.bbox.getSize(new THREE.Vector3()).length() / 400 || 0.1, 12, 12),
      new THREE.MeshBasicMaterial({ color: 0x0e7490 })
    );
    dot.position.copy(point);
    this.measureGroup.add(dot);
    if (this.measurePoints.length === 2) {
      const [a, b] = this.measurePoints;
      const line = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([a, b]),
        new THREE.LineBasicMaterial({ color: 0x0e7490 })
      );
      this.measureGroup.add(line);
      const mm = a.distanceTo(b) * this.mmPerUnit;
      const mid = a.clone().add(b).multiplyScalar(0.5);
      this.measureGroup.add(this._measureLabel(`${Math.round(mm).toLocaleString()} mm`, mid));
      this.onMeasure?.(mm);
      this.measurePoints = [];
    }
    this._markDirty();
  }

  clearMeasure() {
    this.measurePoints = [];
    while (this.measureGroup.children.length) {
      const child = this.measureGroup.children.pop();
      child.geometry?.dispose();
      child.material?.dispose();
    }
    this.onMeasure?.(null);
    this._markDirty();
  }

  stats() {
    return {
      meshes: this.meshes.length,
      panels: this.panelMeta.size,
      triangles: this.meshes.reduce(
        (sum, mesh) => sum + (mesh.geometry.index?.count || 0) / 3 * mesh.count,
        0
      ),
      fps: Math.round(this._fps || 0),
    };
  }

  /** Live render telemetry: draw calls, culling, GPU/JS memory, frame rate. */
  telemetry() {
    const info = this.renderer.info;
    let gpuBytes = 0;
    let instances = 0;
    let visibleMeshes = 0;
    this.meshes.forEach((mesh) => {
      instances += mesh.count;
      if (mesh.visible) visibleMeshes += 1;
      Object.values(mesh.geometry.attributes).forEach((attr) => {
        gpuBytes += attr.array?.byteLength || 0;
      });
      gpuBytes += mesh.geometry.index?.array?.byteLength || 0;
      gpuBytes += mesh.count * 64; // instance matrices
      if (mesh.instanceColor) gpuBytes += mesh.count * 12;
    });
    const heap = performance.memory?.usedJSHeapSize || null;
    return {
      fps: Math.round(this._fps || 0),
      frameMs: this._frameMs ? Math.round(this._frameMs * 100) / 100 : 0,
      drawCalls: info.render.calls,
      trianglesRendered: info.render.triangles,
      geometries: info.memory.geometries,
      textures: info.memory.textures,
      programs: info.programs?.length || 0,
      meshes: this.meshes.length,
      visibleMeshes,
      culledMeshes: this.meshes.length - visibleMeshes,
      instances,
      panels: this.panelMeta.size,
      hiddenPanels: this.hidden.size,
      selected: this.selection.size,
      ghosting: !!this._ghosting,
      gpuMB: Math.round((gpuBytes / 1048576) * 10) / 10,
      heapMB: heap ? Math.round((heap / 1048576) * 10) / 10 : null,
      pixelRatio: this.renderer.getPixelRatio(),
    };
  }

  /** Mark the scene dirty — the next animation frame re-renders once. */
  _markDirty() {
    this._dirty = true;
  }

  /** Zoomed far out, panel labels become unreadable clutter — hide them. */
  _syncLabelZoom() {
    if (!this.labelGroup.children.length || this.bbox.isEmpty()) return;
    const size = this.bbox.getSize(new THREE.Vector3()).length();
    const distance = this.camera.position.distanceTo(this.controls.target);
    const hide = distance > size * 1.6;
    if (this.labelGroup.visible === hide) {
      this.labelGroup.visible = !hide;
      this._dirty = true;
    }
  }

  /** Far out: hide dense inner layer edges, keep only panel boundary outlines. */
  _syncEdgeZoom() {
    if (this.bbox.isEmpty() || !this.edgeGroup) return;
    const size = this.bbox.getSize(new THREE.Vector3()).length();
    const distance = this.camera.position.distanceTo(this.controls.target);
    const far = distance > size * 1.6;
    if (this.edgeGroup.visible === far) {
      this.edgeGroup.visible = !far;
      if (this.boundaryGroup) this.boundaryGroup.visible = far;
      this._dirty = true;
    }
  }

  _animate() {
    this._frame = requestAnimationFrame(() => this._animate());
    const now = performance.now();
    if (this._last) {
      const delta = now - this._last;
      this._fps = this._fps ? this._fps * 0.9 + (1000 / delta) * 0.1 : 1000 / delta;
      this._frameMs = this._frameMs ? this._frameMs * 0.9 + delta * 0.1 : delta;
    }
    this._last = now;
    this._stepCameraTween(now);
    if (this._camTween) this._dirty = true;
    // Notes/issues from the last 24h pulse so fresh input is noticeable.
    if (this._blinkers?.length) {
      const pulse = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(now / 260));
      this._blinkers.forEach((sprite) => {
        sprite.material.opacity = pulse;
      });
      this._dirty = true;
    }
    // update() is cheap and emits "change" (marking dirty) only while moving.
    this.controls.update();
    this._syncLabelZoom();
    this._syncEdgeZoom();
    if (this._dirty) {
      this._dirty = false;
      this.renderer.render(this.scene, this.camera);
      this.viewCube?.update();
    }
  }

  /**
   * Smoothly orbit the camera to a world-space view direction (ViewCube click).
   * Only the camera moves — the model orientation is untouched.
   */
  animateToDirection(direction, duration = 620) {
    const target = this.controls.target.clone();
    const offset = this.camera.position.clone().sub(target);
    const distance = offset.length() || 100;
    const end = direction.clone().normalize();
    // Pure top/bottom views are degenerate with a Z-up camera: nudge slightly.
    if (Math.abs(end.z) > 0.999) end.setY(-0.0025).normalize();
    this._camTween = {
      start: performance.now(),
      duration,
      from: offset.clone().normalize(),
      to: end,
      distance,
      target,
    };
  }

  _stepCameraTween(now) {
    const tween = this._camTween;
    if (!tween) return;
    const raw = Math.min(1, (now - tween.start) / tween.duration);
    const t = raw < 0.5 ? 2 * raw * raw : 1 - (-2 * raw + 2) ** 2 / 2;
    const quaternion = new THREE.Quaternion().setFromUnitVectors(tween.from, tween.to);
    const step = new THREE.Quaternion().slerpQuaternions(new THREE.Quaternion(), quaternion, t);
    const offset = tween.from.clone().applyQuaternion(step).multiplyScalar(tween.distance);
    this.camera.position.copy(tween.target).add(offset);
    this.camera.lookAt(tween.target);
    if (raw >= 1) this._camTween = null;
  }
}
